import React, { useState } from 'react';
import {
    Button, Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay, Spinner, Text
} from '@chakra-ui/react';
import FileDropZone from '../FileDropZone';
import PiiDataViewer from './piiDataViewer';
import fetchWrapper from '../../lib/fetch-wrapper';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useProfile } from '../../lib/profileContext';
import PiiDocumentModal from './PiiDocumentModal';

const ReplaceModal = ({
                          isOpen,
                          onClose,
                          onFileSelect,
                          selectedFile,
                          onUpload,
                      }: {
    isOpen: boolean;
    onClose: () => void;
    onFileSelect: (file: File | null) => void;
    selectedFile: File | null;
    onUpload: () => Promise<void>;
}) => {
    const { instance, accounts }: { instance: IPublicClientApplication; accounts: AccountInfo[] } = useMsal();
    const { profile } = useProfile();

    const [isUploading, setIsUploading] = useState(false);
    const [isFinalUploading, setIsFinalUploading] = useState(false);
    const [piiData, setPiiData] = useState<{ detectedText: string; category: string; page: string; }[]>([]);
    const [fullResults, setFullResults] = useState<any[]>([]);
    const [isPiiConfirmed, setIsPiiConfirmed] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState<any | null>(null);
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);

    const handleFileSelect = async (file: File | null) => {
        setIsUploading(true);
        if (file) {
            const fetchInstance = new fetchWrapper();
            const { flatPiiData, rawResults } = await fetchInstance.checkPiiFile('/api/files/check', file);
            setPiiData(flatPiiData);
            setFullResults(rawResults);
        } else {
            setPiiData([]);
            setFullResults([]);
        }
        onFileSelect(file);
        setIsUploading(false);
    };

    const handleUpload = async () => {
        setIsFinalUploading(true);
        await onUpload();
        setIsFinalUploading(false);
    };

    const closeHandler = () => {
        setPiiData([]);
        setFullResults([]);
        setIsDocModalOpen(false);
        onClose();
    };

    const handlePiiClick = (entity: any) => {
        setSelectedEntity(entity);
        setIsDocModalOpen(true);
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={closeHandler} size="2xl" blockScrollOnMount={false}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Replace File</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FileDropZone onFileSelect={handleFileSelect} selectedFile={selectedFile} />
                        {isUploading && (
                            <Flex alignItems="center" mt={3}>
                                <Spinner size="sm" mr={2} color="blue.500" />
                                <Text fontWeight="bold" color="blue.600">
                                    Uploading file for PII verification. Please wait...
                                </Text>
                            </Flex>
                        )}
                        {piiData.length > 0 && (
                            <PiiDataViewer
                                piiData={piiData}
                                isPiiConfirmed={isPiiConfirmed}
                                togglePiiConfirmed={() => setIsPiiConfirmed((prev) => !prev)}
                                onPiiClick={handlePiiClick}
                            />
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="cancel" onClick={closeHandler} isDisabled={isFinalUploading} mr={3}>
                            Cancel
                        </Button>
                        <Button
                            variant="brand"
                            onClick={handleUpload}
                            isLoading={isFinalUploading}
                            isDisabled={!selectedFile || (!!piiData.length && !isPiiConfirmed) || isFinalUploading}
                        >
                            Upload
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {selectedEntity && fullResults.length > 0 && (
                <PiiDocumentModal
                    isOpen={isDocModalOpen}
                    onClose={() => setIsDocModalOpen(false)}
                    documents={fullResults.map(r => r.document)}
                    allEntities={fullResults.flatMap(r => r.entities)}
                    startPage={String(selectedEntity?.page)}
                />
            )}
        </>
    );
};

export default ReplaceModal;